/**
 * HUD-only UI state (open drawers, overlays, telegram queue, feed filter). Game state lives in
 * `gameStore`; this store never leaves the React layer.
 */
import type { CityId } from '@friction/engine';
import { createStore, useStore } from 'zustand';
import type { DispatchCategory, TelegramSpec } from '../../game/format';
import type { Localized } from '../../i18n/locale';

export type HudPanel = 'production' | 'research' | 'intel' | 'weather';
export type HudOverlay = 'menu' | 'manual' | 'settings' | 'hotkeys' | 'surrender';

/** A telegram card, worded in every language when it arrived (a language switch re-types it). */
export interface Telegram {
  id: number;
  tick: number;
  tone: TelegramSpec['tone'];
  hex: number;
  text: Localized<TelegramSpec>;
}

interface HudState {
  panel: HudPanel | null;
  overlay: HudOverlay | null;
  feedFilter: DispatchCategory | 'all';
  feedExpanded: boolean;
  /** City chosen for new production (null = capital / first eligible). */
  productionCity: CityId | null;
  telegrams: Telegram[];
  /** Game tick at which the intelligence drawer was last looked at (unread-intercept badge). */
  intelSeenAt: number;
  markIntelSeen(tick: number): void;
  /** Short-lived notice under the top bar (e.g. a command the host rejected). */
  notice: { id: number; text: string } | null;
  flashNotice(text: string): void;
  clearNotice(id: number): void;
  togglePanel(p: HudPanel): void;
  setPanel(p: HudPanel | null): void;
  setOverlay(o: HudOverlay | null): void;
  setFeedFilter(f: DispatchCategory | 'all'): void;
  toggleFeed(): void;
  setProductionCity(c: CityId | null): void;
  pushTelegram(t: Localized<TelegramSpec>, tick: number): void;
  dismissTelegram(id: number): void;
  reset(): void;
}

/** At most this many telegram cards on screen; older ones are pushed off. */
const MAX_TELEGRAMS = 3;
let telegramSeq = 1;

export const hudStore = createStore<HudState>()((set) => ({
  panel: null,
  overlay: null,
  feedFilter: 'all',
  feedExpanded: false,
  productionCity: null,
  telegrams: [],
  intelSeenAt: -1,
  markIntelSeen: (intelSeenAt) => set({ intelSeenAt }),
  notice: null,
  flashNotice: (text) => set({ notice: { id: telegramSeq++, text } }),
  clearNotice: (id) => set((s) => (s.notice?.id === id ? { notice: null } : s)),
  // A drawer and the expanded teletype share the left column: opening one folds the other.
  togglePanel: (p) => set((s) => (s.panel === p ? { panel: null } : { panel: p, feedExpanded: false })),
  setPanel: (panel) => set(panel ? { panel, feedExpanded: false } : { panel }),
  setOverlay: (overlay) => set({ overlay }),
  setFeedFilter: (feedFilter) => set({ feedFilter }),
  toggleFeed: () =>
    set((s) => (s.feedExpanded ? { feedExpanded: false } : { feedExpanded: true, panel: null })),
  setProductionCity: (productionCity) => set({ productionCity }),
  pushTelegram: (t, tick) =>
    set((s) => ({
      telegrams: [...s.telegrams, { id: telegramSeq++, tick, tone: t.en.tone, hex: t.en.hex, text: t }].slice(
        -MAX_TELEGRAMS,
      ),
    })),
  dismissTelegram: (id) => set((s) => ({ telegrams: s.telegrams.filter((t) => t.id !== id) })),
  reset: () =>
    set({
      panel: null,
      overlay: null,
      telegrams: [],
      productionCity: null,
      feedExpanded: false,
      intelSeenAt: -1,
    }),
}));

export function useHud<T>(selector: (s: HudState) => T): T {
  return useStore(hudStore, selector);
}
